import React from "react";
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, FileJson, Search } from "lucide-react";
import { fetchCertificates } from "../services/apiServices";
import { useCbomContext } from "../context/CbomContext";
import JsonModal from "./JsonModal";
import { formatDate } from "../utils/formatDate";
import { sortData } from "../utils/sortData";
import { CbomCertificate as CbomCertificateData } from "@schemas";
import { useProjectContext } from "context/ProjectContext";

interface CbomCertificate {
  c_id: number;
  device_id: string;
  file_path: string;
  serial_number: string;
  issue_date: string | Date;
  expiry_date: string | Date;
  original_signature_algorithm: string;
  hashing_algorithm: string;
  signature_algorithm: string;
  issuer: string;
  validity_status: string;
  time_of_expiry_days: number;
  crl_status: string;
  ocsp_status: string;
  subject: string;
  public_key_algorithm: string;
  public_key_length: number;
  key_usage: string;
  extended_key_usage: string;
  version: number;
  signature_algorithm_qv: string;
  public_key_algorithm_qv: string;
  cbom_json: any; // Adjust this type if the structure of the JSON is known
}

const cbomCertificateFieldLabels: Record<keyof CbomCertificate, string> = {
  c_id: "C. ID",
  device_id: "Device ID",
  file_path: "File Path",
  serial_number: "Serial Number",
  issue_date: "Issue Date",
  expiry_date: "Expiry Date",
  original_signature_algorithm: "Original Signature Algorithm",
  signature_algorithm: "Signature Algorithm",
  hashing_algorithm: "Hashing Algorithm",
  issuer: "Issuer",
  validity_status: "Validity Status",
  time_of_expiry_days: "Time to Expiry (Days)",
  crl_status: "CRL Status",
  ocsp_status: "OCSP Status",
  subject: "Subject",
  public_key_algorithm: "Public Key Algorithm",
  public_key_length: "Public Key Length",
  key_usage: "Key Usage",
  extended_key_usage: "Extended Key Usage",
  version: "Version",
  signature_algorithm_qv: "Signature Algorithm (QV)",
  public_key_algorithm_qv: "Public Key Algorithm (QV)",
  cbom_json: "CBOM JSON",
};

interface CertificatesTableProps {
  customData?: CbomCertificateData[];
}
const CertificatesTable: React.FC<CertificatesTableProps> = ({
  customData,
}) => {
  const { state } = useProjectContext();
  const { selectedDeviceId, showCbomComponent } = useCbomContext();
  const cbomCertificateFields = Object.keys(
    cbomCertificateFieldLabels
  ) as (keyof CbomCertificate)[];
  const [certificatesData, setCertificatesData] = useState<CbomCertificate[]>(
    []
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredSources, setFilteredSources] = useState<CbomCertificate[]>([]);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSources.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSources.length / itemsPerPage);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jsonData, setJsonData] = useState(null);
  const [sortField, setSortField] = useState<keyof CbomCertificate>("c_id");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };
  useEffect(() => {
    if (customData) {
      const transformedData = customData.map((c) => ({
        c_id: c.c_id ?? 0,
        device_id: c.device_id ?? "",
        file_path: c.file_path ?? "",
        serial_number: c.serial_number ?? "",
        issue_date: c.issue_date ?? "",
        expiry_date: c.expiry_date ?? "",
        original_signature_algorithm: c.original_signature_algorithm ?? "",
        hashing_algorithm: c.hashing_algorithm ?? "",
        signature_algorithm: c.signature_algorithm ?? "",
        issuer: c.issuer ?? "",
        validity_status: c.validity_status ?? "",
        time_of_expiry_days: c.time_of_expiry_days ?? 0,
        crl_status: c.crl_status ?? "",
        ocsp_status: c.ocsp_status ?? "",
        subject: c.subject ?? "",
        public_key_algorithm: c.public_key_algorithm ?? "",
        public_key_length: c.public_key_length ?? 0,
        key_usage: c.key_usage ?? "",
        extended_key_usage: c.extended_key_usage ?? "",
        version: c.version ?? 0,
        signature_algorithm_qv: c.signature_algorithm_qv ?? "",
        public_key_algorithm_qv: c.public_key_algorithm_qv ?? "",
        cbom_json: c.cbom_json ?? {},
      }));

      const sorted = sortData<CbomCertificate>(transformedData, "c_id", "asc");
      setCertificatesData(sorted);
    } else {
      if (state.currentProjectID)
        fetchCertificates(
          (data) => {
            const sorted = sortData<CbomCertificate>(data, "c_id", "asc");
            setCertificatesData(sorted);
          },
          selectedDeviceId,
          showCbomComponent,
          state.currentProjectID
        );
    }
  }, [selectedDeviceId, showCbomComponent, state.currentProjectID]);
  useEffect(() => {
    if (sortField) {
      const sortedData = sortData(filteredSources, sortField, sortOrder);
      setFilteredSources(sortedData);
    }
  }, [sortField, sortOrder]);
  useEffect(() => {
    if (searchTerm) {
      const filtered = certificatesData.filter(
        (e) =>
          e &&
          Object.values(e)
            .filter(
              (value) => typeof value === "string" || typeof value === "number"
            )
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredSources(filtered);
    } else {
      setFilteredSources(certificatesData); // Show all data if search term is cleared
    }
  }, [searchTerm, certificatesData]);
  const nextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };
  const prevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };
  const handleJsonClick = (json: any) => {
    setJsonData(json);
    setIsModalOpen(true);
  };
  const handleSort = (field: keyof CbomCertificate) => {
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };
  return (
    <>
      <div className="flex flex-col h-full bg-custom-blue8 border rounded-md p-2">
        <div className="flex justify-between items-center mb-4">
          {/* <h1 className="text-xl font-bold text-custom-blue7 pl-2">
            Certificates Information
          </h1> */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearch}
              className="pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-custom-blue5 bg-white"
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
          </div>
          {location.hash === "#/overallSummary" && <div></div>}
        </div>
        <div className="flex-1 overflow-auto custom-scrollbar">
          <table
            id="data-table"
            className="min-w-full bg-white shadow custom-scrollbar border rounded-md"
          >
            <thead className="sticky top-0 z-10 bg-custom-blue0">
              <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                {cbomCertificateFields.map((field) => {
                  let headerClass = "p-2";
                  let headerText = cbomCertificateFieldLabels[field];

                  if (field === "file_path") {
                    headerClass +=
                      " max-w-[200px] whitespace-normal break-words";
                    headerText = "File Path";
                  } else if (field === "serial_number") {
                    headerClass +=
                      " max-w-[150px] whitespace-normal break-words";
                    headerText = "Serial Number";
                  }
                  return (
                    <th
                      key={field}
                      className={headerClass}
                      onClick={() => handleSort(field)}
                    >
                      {headerText}
                      {sortField === field && (
                        <span className="ml-1">
                          {sortOrder === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {currentItems &&
              Array.isArray(currentItems) &&
              currentItems.length > 0 ? (
                currentItems.map((source, index) => {
                  return (
                    <tr
                      key={source.c_id}
                      className={`${
                        index % 2 === 1 ? "bg-custom-blue0" : ""
                      } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                    >
                      {cbomCertificateFields.map((field) => (
                        <td
                          key={field}
                          className={`p-2 min-w-[100px] ${
                            field === "file_path"
                              ? "max-w-[200px] break-words whitespace-normal"
                              : [
                                  "serial_number",
                                  "issue_date",
                                  "expiry_date",
                                ].includes(field)
                              ? "max-w-[150px] break-words whitespace-normal"
                              : ["c_id", "device_id"].includes(field)
                              ? "min-w-[50px]"
                              : ""
                          }`}
                        >
                          {field === "cbom_json" && source.cbom_json ? (
                            <button
                              className="text-blue-600 hover:text-custom-blue7 flex items-center gap-1"
                              onClick={() => handleJsonClick(source.cbom_json)}
                            >
                              <FileJson />
                            </button>
                          ) : field === "issue_date" ||
                            field === "expiry_date" ? (
                            formatDate(source[field])
                          ) : source[field] ? (
                            String(source[field])
                          ) : (
                            "N/A"
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={showCbomComponent ? 20 : 15}
                    className="text-custom-blue7 p-2 text-center"
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between items-center mt-2">
          <div className="flex items-center space-x-2">
            <select
              className="border rounded-md p-2 bg-white"
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={40}>40</option>
              <option value={50}>50</option>
            </select>
            <span className="text-custom-blue7">Results per page</span>
          </div>
          <div className="flex items-center space-x-2 text-custom-blue7">
            <span>
              Displaying {indexOfFirstItem + 1}-
              {Math.min(indexOfLastItem, filteredSources.length)} of{" "}
              {filteredSources.length}
            </span>
            <button
              className="p-2 bg-white rounded-md"
              onClick={prevPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className="p-2 bg-white rounded-md"
              onClick={nextPage}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
        {isModalOpen && (
          <JsonModal
            data={jsonData}
            onClose={() => setIsModalOpen(false)}
            title="CBOM Details"
          />
        )}
      </div>
    </>
  );
};

export default CertificatesTable;
